import { useCreateProviderDeployment } from '@metorial/state';
import { Badge, Callout } from '@metorial/ui';
import { RiHammerLine } from '@remixicon/react';
import { useEffect, useRef, useState } from 'react';

export let PROVIDER_BUILDING_HINT =
  'This integration is still being built. You can already add it; its tools, auth methods and config become available once the build finishes.';

/**
 * BlackOS lists every library integration straight from its package metadata and builds
 * it in the background (see `./scripts/integrations-status.sh`). Until the build is
 * discovered the provider has no current version, so it has no tools or auth methods yet.
 */
export let isProviderBuilding = (
  provider: { currentVersion?: unknown | null } | null | undefined
) => !!provider && !provider.currentVersion;

export let ProviderBuildingBadge = ({ size = '1' }: { size?: '1' | '2' }) => (
  <span title={PROVIDER_BUILDING_HINT} style={{ display: 'inline-flex' }}>
    <Badge size={size} color="orange">
      <RiHammerLine size={12} style={{ marginRight: 3 }} /> Building
    </Badge>
  </span>
);

/**
 * Refetches a provider every few seconds while its build is running, so screens waiting
 * for tools or auth methods update on their own. Returns whether it is still building.
 */
export let useRefetchProviderWhileBuilding = (
  provider: { data?: { currentVersion?: unknown | null } | null; refetch?: () => any },
  intervalMs = 10_000
) => {
  let building = isProviderBuilding(provider.data);
  let refetchRef = useRef(provider.refetch);
  refetchRef.current = provider.refetch;

  useEffect(() => {
    if (!building) return;
    let timer = window.setInterval(() => refetchRef.current?.(), intervalMs);
    return () => window.clearInterval(timer);
  }, [building, intervalMs]);

  return building;
};

/**
 * Creating a provider deployment for an integration that isn't built yet moves its build to
 * the front of the queue (hub `slate.requestBuild`). Setup flows call this so picking an
 * unbuilt integration starts its build right away; the returned deployment id should be
 * reused when the flow submits instead of creating a second deployment.
 */
export let useRequestProviderBuild = (p: {
  instanceId: string | null | undefined;
  providerId: string | null | undefined;
  building: boolean;
  deploymentName: string;
}) => {
  let createDeployment = useCreateProviderDeployment();
  let [deploymentId, setDeploymentId] = useState<string | null>(null);
  let requestedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!p.building || !p.instanceId || !p.providerId) return;
    if (requestedForRef.current === p.providerId) return;
    requestedForRef.current = p.providerId;

    let providerId = p.providerId;
    void createDeployment
      .mutate({ instanceId: p.instanceId, providerId, name: p.deploymentName })
      .then(([deployment]) => {
        if (deployment && requestedForRef.current === providerId) {
          setDeploymentId(deployment.id);
        }
      });
  }, [p.building, p.instanceId, p.providerId]);

  return deploymentId;
};

export let ProviderBuildingNotice = ({ providerName }: { providerName?: string | null }) => (
  // Callout renders its children inside a <p>, so only inline elements here.
  <Callout color="orange">
    {providerName ?? 'This integration'} is still being built. Its build has been moved to the
    front of the queue and usually finishes within a few minutes; this updates on its own once
    its tools and auth methods are available.
  </Callout>
);
